// AdminClearanceTabs.jsx (corrected)
import React, { useState, useRef } from 'react'
import AdminImportsForm from './AdminImportsForm.jsx'
import AdminExportsForm from './AdminExportsForm.jsx'
import AdminFreightForm from './AdminFreightForm.jsx'
import { clearanceService } from '../services/clearanceService.js'
import { uploadDocumentToGCS, deleteDocument } from '../utils/documentUpload.js'

export default function AdminClearanceTabs({ customerInfo, onResponse }) {
  const [active, setActive] = useState('imports')
  const [submitting, setSubmitting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploadingDocs, setUploadingDocs] = useState({})
  const [draftRequestId, setDraftRequestId] = useState(null)

  const formDataRef = useRef({ imports: null, exports: null, freight: null })
  const creatingDraftRef = useRef(false)

  const DOCUMENT_TYPE_MAP = {
    commonInvoices: 'commercial_invoice',
    packingLists: 'packing_list',
    billOfLading: 'bill_of_lading',
    insurance: 'insurance',
    ex_works: 'ex_works',
    others: 'others',
  }

  // Helper to build API payload from form data
  const buildPayload = (formType, data) => {
    const base = {
      clearingAgentNum: data.clearingAgentNum || null,
      customer: customerInfo,
    }

    if (formType === 'imports') {
      // data shape: { city, transportMode, containerType, port, files, ... }
      const transportMode = data.city === 'LHR' ? 'air_only' : data.transportMode
      return {
        ...base,
        requestType: 'import',
        city: data.city || null,
        transportMode: transportMode || null,
        containerType: data.containerType || null,
        port: data.port || null,
      }
    }

    if (formType === 'exports') {
      // data shape: { city, transportMode, containerType, files: { pol, pod, product, incoTerms, ... }, ... }
      const transportMode = data.city === 'LHR' ? 'air_only' : data.transportMode
      return {
        ...base,
        requestType: 'export',
        city: data.city || null,
        transportMode: transportMode || null,
        containerType: data.containerType || null,
        pol: data.files?.pol || null,
        pod: data.files?.pod || null,
        product: data.files?.product || null,
        incoterms: data.files?.incoTerms || null,
      }
    }

    if (formType === 'freight') {
      // data shape: { tradeType, containerType, fields: { pol, pod, product, incoTerms, cbm, pkgs, containerSize, numberOfContainers }, ... }
      const payload = {
        ...base,
        requestType: 'freight_forwarding',
        containerType: data.containerType || null,
        pol: data.fields?.pol || null,
        pod: data.fields?.pod || null,
        product: data.fields?.product || null,
        incoterms: data.fields?.incoTerms || null,
      }

      if (data.containerType === 'LCL') {
        payload.cbm = data.fields?.cbm ? parseFloat(data.fields.cbm) : null
        payload.packages = data.fields?.pkgs ? parseInt(data.fields.pkgs, 10) : null
      }

      if (data.containerType === 'FCL') {
        payload.containerSize = data.fields?.containerSize || null
        payload.numberOfContainers = data.fields?.numberOfContainers
          ? parseInt(data.fields.numberOfContainers, 10)
          : null
      }

      return payload
    }

    return base
  }

  const ensureDraftExists = async (formType, formData) => {
    if (draftRequestId) return draftRequestId
    if (creatingDraftRef.current) return creatingDraftRef.current

    if (!customerInfo || !customerInfo.name) {
      alert('Customer information is required')
      throw new Error('Customer info missing')
    }

    creatingDraftRef.current = (async () => {
      try {
        setSaving(true)
        const payload = buildPayload(formType, formData)
        const response = await clearanceService.create(payload)
        if (!response.success) throw new Error(response.message)

        const request = response.data.request || response.data
        setDraftRequestId(request.id)

        if (onResponse) {
          onResponse({
            userCreated: response.data.userCreated,
            tempPassword: response.data.tempPassword,
          })
        }
        return request.id
      } catch (err) {
        console.error('Failed to create draft:', err)
        alert(err.message || 'Could not create draft')
        throw err
      } finally {
        setSaving(false)
        creatingDraftRef.current = false
      }
    })()
    return creatingDraftRef.current
  }

  const handleDocumentUpload = async (file, docTypeKey, existingDocId, onProgress) => {
    let targetId = draftRequestId
    if (!targetId) {
      const formType = active
      const latestFormData = formDataRef.current[formType] || {}
      targetId = await ensureDraftExists(formType, latestFormData)
      if (!targetId) throw new Error('Could not create draft')
    }

    const documentType = DOCUMENT_TYPE_MAP[docTypeKey]
    if (!documentType) throw new Error(`Unknown document type: ${docTypeKey}`)

    try {
      setUploadingDocs(prev => ({ ...prev, [docTypeKey]: true }))
      await uploadDocumentToGCS(targetId, file, documentType, onProgress)
      if (existingDocId) {
        try { await deleteDocument(existingDocId) } catch (e) { console.warn('Failed to delete old doc', e) }
      }
    } catch (err) {
      console.error('Document upload failed:', err)
      throw err
    } finally {
      setUploadingDocs(prev => {
        const updated = { ...prev }
        delete updated[docTypeKey]
        return updated
      })
    }
  }

  const handleFormChange = (formType, formData) => {
    formDataRef.current[formType] = formData
  }

  const handleSubmit = async (formType, formData) => {
  let targetId = draftRequestId;
  if (!targetId) {
    // No draft yet, create one now using the current form data
    try {
      targetId = await ensureDraftExists(formType, formData);
      if (!targetId) throw new Error('Could not create draft');
    } catch (err) {
      console.error(err);
      alert('Failed to create draft before submission');
      return;
    }
  }

  setSubmitting(true);
  try {
    // Remove files/fields from payload — they're already uploaded via presign
    const { files, fields, ...dataWithoutFiles } = formData;
    const payload = { ...dataWithoutFiles, status: 'SUBMITTED' };
    const updateResult = await clearanceService.update(targetId, payload);
    if (!updateResult.success) throw new Error(updateResult.message);

    onResponse({ success: true, submitted: true });
  } catch (err) {
    console.error(err);
    alert(err.message || 'Submission failed');
  } finally {
    setSubmitting(false);
  }
};

  return (
    <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-[#e9ecef] flex items-center gap-3">
        <h2 className="text-base font-semibold text-[#2c3e50]">Clearance Forms</h2>
        {saving && <span className="text-xs text-[#7f8c8d]">Saving draft…</span>}
      </div>
      <div className="p-6">
        <div className="mb-5">
          <div className="inline-flex rounded-lg bg-[#f8f9fa] border border-[#e9ecef] p-1 gap-1">
            <button
              type="button"
              onClick={() => setActive('imports')}
              className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
                active === 'imports' ? 'bg-[#2c3e50] text-white' : 'text-[#7f8c8d] hover:text-[#2c3e50]'
              }`}
            >
              Import
            </button>
            <button
              type="button"
              onClick={() => setActive('exports')}
              className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
                active === 'exports' ? 'bg-[#2c3e50] text-white' : 'text-[#7f8c8d] hover:text-[#2c3e50]'
              }`}
            >
              Export
            </button>
            <button
              type="button"
              onClick={() => setActive('freight')}
              className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
                active === 'freight' ? 'bg-[#2c3e50] text-white' : 'text-[#7f8c8d] hover:text-[#2c3e50]'
              }`}
            >
              Freight Forwarding
            </button>
          </div>
        </div>

        <div>
          {active === 'imports' && (
            <AdminImportsForm
              onSubmit={(data) => handleSubmit('imports', data)}
              disabled={submitting}
              onUserChange={(data) => handleFormChange('imports', data)}
              onDocumentUpload={handleDocumentUpload}
              uploadingDocs={uploadingDocs}
              customerInfo={customerInfo}
              ensureDraft={() => ensureDraftExists('imports', formDataRef.current.imports || {})}
            />
          )}
          {active === 'exports' && (
            <AdminExportsForm
              onSubmit={(data) => handleSubmit('exports', data)}
              disabled={submitting}
              onUserChange={(data) => handleFormChange('exports', data)}
              onDocumentUpload={handleDocumentUpload}
              uploadingDocs={uploadingDocs}
              customerInfo={customerInfo}
              ensureDraft={() => ensureDraftExists('exports', formDataRef.current.exports || {})}
            />
          )}
          {active === 'freight' && (
            <AdminFreightForm
              onSubmit={(data) => handleSubmit('freight', data)}
              disabled={submitting}
              onUserChange={(data) => handleFormChange('freight', data)}
              onDocumentUpload={handleDocumentUpload}
              uploadingDocs={uploadingDocs}
              customerInfo={customerInfo}
              ensureDraft={() => ensureDraftExists('freight', formDataRef.current.freight || {})}
            />
          )}
        </div>
      </div>
    </div>
  )
}